<script setup>
import { ref, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const fields = computed({
  get: () => props.modelValue || [],
  set: val => emit('update:modelValue', val),
});

const isModalOpen = ref(false);
const editingIndex = ref(-1);
const rawEnumOptions = ref('');
const fieldForm = ref({
  nome_interno: '',
  pergunta: '',
  tipo: 'texto',
  obrigatorio: true,
  opcoes: [],
  ordem: 0,
});

const openAddField = () => {
  editingIndex.value = -1;
  rawEnumOptions.value = '';
  fieldForm.value = {
    nome_interno: '',
    pergunta: '',
    tipo: 'texto',
    obrigatorio: true,
    opcoes: [],
    ordem: fields.value.length,
  };
  isModalOpen.value = true;
};

const openEditField = (field, index) => {
  editingIndex.value = index;
  rawEnumOptions.value = (field.opcoes || []).join('\n');
  fieldForm.value = { ...field };
  isModalOpen.value = true;
};

const saveField = () => {
  if (!fieldForm.value.nome_interno.trim() || !fieldForm.value.pergunta.trim())
    return;

  if (fieldForm.value.tipo === 'enum') {
    fieldForm.value.opcoes = rawEnumOptions.value
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
  } else {
    fieldForm.value.opcoes = [];
  }

  const list = [...fields.value];
  if (editingIndex.value >= 0) {
    list[editingIndex.value] = { ...fieldForm.value };
  } else {
    list.push({ ...fieldForm.value, ordem: list.length });
  }
  fields.value = list;
  isModalOpen.value = false;
};

const removeField = index => {
  const list = [...fields.value];
  const item = list[index];
  if (item.id) {
    item._destroy = true;
  } else {
    list.splice(index, 1);
  }
  fields.value = list;
};

const visibleFields = computed(() => {
  return fields.value.filter(f => !f._destroy);
});
</script>

<template>
  <div class="space-y-4 max-w-4xl">
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            Campos de Coleta de Dados do Lead
          </h3>
          <p class="text-xs text-n-slate-11">
            Defina o schema ordenado de informações que a Isa Leal deve extrair
            durante a conversa.
          </p>
        </div>
        <Button
          sm
          icon="i-lucide-plus"
          label="Adicionar Campo"
          @click="openAddField"
        />
      </div>

      <div
        v-if="visibleFields.length === 0"
        class="text-center py-8 border border-dashed border-n-weak rounded-lg text-n-slate-11 text-sm"
      >
        Nenhum campo de coleta configurado. Clique em "+ Adicionar Campo" para
        criar.
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="(field, index) in visibleFields"
          :key="field.id || index"
          class="bg-n-surface-2 border border-n-weak rounded-xl p-4 flex items-center justify-between gap-4 hover:border-n-brand/50 transition-colors"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <span
              class="size-7 rounded-lg bg-n-surface-1 border border-n-weak text-n-slate-12 font-mono text-xs flex items-center justify-center font-bold"
            >
              {{ index + 1 }}
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="font-mono font-bold text-sm text-n-slate-12">{{
                  field.nome_interno
                }}</span>
                <span
                  class="px-2 py-0.5 rounded text-[10px] font-semibold bg-n-brand/10 text-n-brand uppercase"
                >
                  {{ field.tipo }}
                </span>
                <span
                  v-if="field.obrigatorio"
                  class="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 text-red-500"
                >
                  Obrigatório
                </span>
              </div>
              <p class="text-xs text-n-slate-11 truncate mt-0.5">
                {{ field.pergunta }}
              </p>
              <div
                v-if="field.tipo === 'enum' && field.opcoes?.length"
                class="flex flex-wrap gap-1 mt-1.5"
              >
                <span
                  v-for="opt in field.opcoes"
                  :key="opt"
                  class="px-1.5 py-0.5 rounded text-[10px] bg-n-surface-1 border border-n-weak text-n-slate-11 font-mono"
                >
                  {{ opt }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center space-x-1">
            <Button
              ghost
              slate
              sm
              icon="i-lucide-pencil"
              @click="openEditField(field, index)"
            />
            <Button
              ghost
              ruby
              sm
              icon="i-lucide-trash-2"
              @click="removeField(index)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Adicionar / Editar Campo -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
    >
      <div
        class="bg-n-surface-1 border border-n-weak rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-surface-2"
        >
          <h3 class="font-bold text-base text-n-slate-12">
            {{
              editingIndex >= 0
                ? 'Editar Campo de Coleta'
                : 'Novo Campo de Coleta'
            }}
          </h3>
          <Button
            ghost
            slate
            sm
            icon="i-lucide-x"
            @click="isModalOpen = false"
          />
        </div>

        <div class="p-6 space-y-4">
          <div>
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Nome Interno (Chave JSON / Variável no n8n)
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="fieldForm.nome_interno"
              type="text"
              placeholder="Ex: nomeResponsavel, dataNascimento, segmento, escolaAtual"
              class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
              required
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Pergunta / Instrução de Coleta <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="fieldForm.pergunta"
              rows="2"
              placeholder="Ex: Qual é o nome completo do responsável?"
              class="reset-base !mb-0 w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg p-3 text-n-slate-12 focus:border-n-brand outline-none resize-y"
              required
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
                Tipo do Campo
              </label>
              <select
                v-model="fieldForm.tipo"
                class="reset-base !mb-0 w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
              >
                <option value="texto">Texto</option>
                <option value="data">Data</option>
                <option value="enum">Opções (Enum)</option>
                <option value="numero">Número</option>
                <option value="booleano">Booleano (Sim/Não)</option>
              </select>
            </div>

            <div class="flex items-center pt-6">
              <label
                class="flex items-center gap-2 cursor-pointer text-xs font-semibold text-n-slate-12"
              >
                <input
                  v-model="fieldForm.obrigatorio"
                  type="checkbox"
                  class="size-4 rounded text-n-brand focus:ring-0"
                />
                Campo Obrigatório
              </label>
            </div>
          </div>

          <div v-if="fieldForm.tipo === 'enum'">
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Opções Permitidas (1 por linha)
            </label>
            <textarea
              v-model="rawEnumOptions"
              rows="4"
              placeholder="Infantil&#10;Fundamental&#10;Médio"
              class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg p-3 text-n-slate-12 focus:border-n-brand outline-none resize-y"
            />
          </div>
        </div>

        <div
          class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak bg-n-surface-2"
        >
          <Button ghost slate label="Cancelar" @click="isModalOpen = false" />
          <Button label="Salvar Campo" @click="saveField" />
        </div>
      </div>
    </div>
  </div>
</template>
